import type { FC, ReactElement } from 'react';

import { isNumber, isString, startCase, upperFirst } from 'lodash-es';

import type { FacetValues } from '@iconik-io/shared-lib/src/redux/common_filters/types';
import type { FacetsSchema } from '@iconik-io/shared-lib/src/redux/common_list/types';

import CheckboxGroupFieldWrapper from '../../ui/forms/field_wrappers/checkbox_group/checkbox_group';
import Label, { VARIANTS } from '../../ui/label/label';
import numberFormatter from '../../utils/number_formatter';
import SearchFiltersSeparator from '../search_filters_separator/search_filters_separator';

import styles from './search_facets.module.css';

type BucketOption = {
  label: string;
  value: string;
  doc_count?: number;
};

type Props = {
  labelFormatter?: (facetKey: string, bucketKey: string) => string;
  availableFacets?: FacetsSchema;
  facetValues: FacetValues;
  hideCountWhenSelected?: boolean;
  labels?: Record<string, string>;
  onChange?: () => void;
  prefix?: string;
};

const SearchFacets: FC<Props> = ({
  availableFacets = {},
  facetValues,
  hideCountWhenSelected = true,
  labels = {},
  onChange = () => {},
  labelFormatter,
  prefix = 'facets_filters',
}) => {
  const facets: ReactElement[] = [];
  for (const [key, facet] of Object.entries(availableFacets)) {
    const facetBuckets = Array.isArray(facet.buckets) ? [...facet.buckets] : [];

    // Display switches when there is no search result but values.
    // The user should be able to switch it back
    if (facetBuckets.length === 0 && Object.keys(facetValues).length > 0) {
      const facetValue = facetValues[key] ?? [];
      for (const item of facetValue) {
        facetBuckets.push({ doc_count: 0, key: item });
      }
    }

    const buckets: BucketOption[] = facetBuckets.map((bucket) => {
      const bucketKey = bucket.key_as_string ?? bucket.key;
      let label;
      if (isNumber(bucketKey)) {
        label = numberFormatter.format(bucketKey);
      } else if (labelFormatter) {
        label = labelFormatter(key, bucketKey);
      } else if (labels[bucketKey]) {
        label = labels[bucketKey];
      } else {
        label = key.startsWith('metadata') ? bucketKey : upperFirst(bucketKey);
      }
      return {
        label,
        value: String(bucket.key),
        doc_count: bucket.doc_count,
      };
    });

    // If there is no selected value in the facet buckets - prepend it
    const facetBucketKeys = facetBuckets.map((item) => item.key.toString());
    const facetValue = facetValues[key];
    if (facetValue) {
      facetValue
        .filter((item) => !facetBucketKeys.includes(item))
        .forEach((item) => {
          buckets.unshift({
            label: labels[item] ?? item,
            value: String(item),
          });
        });
    }

    if (buckets.length === 0) {
      continue;
    }

    let heading: string;
    if (facet.facetLabel) {
      heading = isString(facet.facetLabel) ? facet.facetLabel : '';
    } else if (key.includes('.')) {
      const keyArray = key.split('.');
      heading = startCase(keyArray[1]);
    } else {
      heading = startCase(key);
    }
    facets.push(
      <fieldset key={key} className={styles.facets_group}>
        <legend className={styles.heading}>
          <strong>{heading}</strong>
        </legend>
        <CheckboxGroupFieldWrapper
          name={`${prefix || ''}${prefix ? '.' : ''}${key}`}
          value={facetValue}
          onChange={onChange}
          options={buckets}
          labelClassName={styles.facet_label}
          checkboxClassName={styles.facet_control}
          componentWrapper={(option: BucketOption, component: ReactElement) => (
            <>
              {component}
              {hideCountWhenSelected && facetValue?.length ? null : (
                <Label className={styles.label} variant={VARIANTS.GRAY}>
                  {option.doc_count}
                </Label>
              )}
            </>
          )}
        />
      </fieldset>,
    );
    facets.push(<SearchFiltersSeparator key={`${key}-separator`} />);
  }

  // Remove the last separator
  if (facets.length > 0) {
    facets.pop();
  }

  return <div>{facets}</div>;
};

export default SearchFacets;
